import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CoverImage from './CoverImage';
import { trackEvent } from '../services/analytics';
import { addToReadingList } from '../services/readingList';
import '../styles/SwipeDiscovery.css';

const QUEUE_TARGET = 3;
const SWIPE_THRESHOLD = 110;

function SwipeDiscovery({ onClose }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [exiting, setExiting] = useState(null);
  const dragState = useRef({ active: false, startX: 0 });
  const seenIdsRef = useRef(new Set());
  const exitTimerRef = useRef(null);

  const fetchOne = useCallback(async () => {
    const response = await fetch('/api/books/random');
    if (!response.ok) {
      throw new Error('No available titles found');
    }
    const data = await response.json();
    if (!data?.book?.biblio_id) {
      throw new Error('No available titles found');
    }
    return data.book;
  }, []);

  const topUpQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = [];
      let attempts = 0;
      const maxAttempts = QUEUE_TARGET * 10;
      while (next.length < QUEUE_TARGET && attempts < maxAttempts) {
        attempts += 1;
        const book = await fetchOne();
        if (!seenIdsRef.current.has(book.biblio_id)) {
          seenIdsRef.current.add(book.biblio_id);
          next.push(book);
        }
      }
      if (next.length > 0) {
        setQueue((prev) => [...prev, ...next]);
      } else if (seenIdsRef.current.size === 0) {
        setError('No books available right now.');
      }
    } catch (err) {
      setError('Could not load more books right now.');
      console.error('Swipe discovery fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchOne]);

  useEffect(() => {
    topUpQueue();
    trackEvent('swipe_discovery_opened', { source: 'home-discovery' });
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && queue.length < QUEUE_TARGET) {
      topUpQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.length, loading]);

  const advance = (direction) => {
    const book = queue[0];
    if (!book) {
      return;
    }

    if (direction === 'right') {
      addToReadingList(book);
      trackEvent('swipe_discovery_liked', { biblioId: book.biblio_id });
    } else {
      trackEvent('swipe_discovery_skipped', { biblioId: book.biblio_id });
    }

    setExiting(direction);
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
    }
    exitTimerRef.current = setTimeout(() => {
      setQueue((prev) => prev.slice(1));
      setExiting(null);
      setDragX(0);
    }, 220);
  };

  const handleOpenDetail = (book) => {
    trackEvent('swipe_discovery_detail_opened', { biblioId: book.biblio_id });
    navigate(`/book/${book.biblio_id}`, { state: { from: 'swipe-discovery' } });
  };

  const onPointerDown = (e) => {
    dragState.current = { active: true, startX: e.clientX };
  };

  const onPointerMove = (e) => {
    if (!dragState.current.active) {
      return;
    }
    setDragX(e.clientX - dragState.current.startX);
  };

  const endDrag = () => {
    if (!dragState.current.active) {
      return;
    }
    dragState.current.active = false;
    if (dragX > SWIPE_THRESHOLD) {
      advance('right');
    } else if (dragX < -SWIPE_THRESHOLD) {
      advance('left');
    } else {
      setDragX(0);
    }
  };

  const topBook = queue[0];
  const nextBook = queue[1];
  const rotation = dragX / 16;

  return (
    <div className="swipe-discovery-overlay" role="dialog" aria-label="Swipe to discover books">
      <div className="swipe-discovery-modal">
        <button type="button" className="swipe-discovery-close" onClick={onClose} aria-label="Close swipe discovery">
          ✕
        </button>
        <h2 className="swipe-discovery-title">🔥 Swipe to Discover</h2>
        <p className="swipe-discovery-hint">Swipe right (or ➜) to save, left (or ⬅) to skip, tap the cover to view details.</p>

        <div className="swipe-discovery-stack">
          {!topBook && !loading && !error && (
            <p className="swipe-discovery-empty">No more books to show right now.</p>
          )}
          {error && !topBook && <p className="swipe-discovery-empty">{error}</p>}

          {nextBook && (
            <div className="swipe-card swipe-card--behind">
              <CoverImage
                biblioId={nextBook.biblio_id}
                isbn={nextBook.isbn}
                alt={nextBook.title}
                className="swipe-card__cover"
                width={200}
                height={290}
                sizeCode="L"
              />
            </div>
          )}

          {topBook && (
            <div
              className={`swipe-card${exiting ? ` swipe-card--exit-${exiting}` : ''}`}
              style={!exiting ? { transform: `translateX(${dragX}px) rotate(${rotation}deg)` } : undefined}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerLeave={endDrag}
            >
              <button
                type="button"
                className="swipe-card__cover-btn"
                onClick={() => handleOpenDetail(topBook)}
              >
                <CoverImage
                  biblioId={topBook.biblio_id}
                  isbn={topBook.isbn}
                  alt={topBook.title}
                  className="swipe-card__cover"
                  width={200}
                  height={290}
                  sizeCode="L"
                />
              </button>
              <p className="swipe-card__title">{topBook.title}</p>
              {topBook.author && <p className="swipe-card__author">by {topBook.author}</p>}
              {dragX > 30 && <span className="swipe-card__badge swipe-card__badge--like">SAVE</span>}
              {dragX < -30 && <span className="swipe-card__badge swipe-card__badge--skip">SKIP</span>}
            </div>
          )}
        </div>

        <div className="swipe-discovery-controls">
          <button type="button" className="swipe-discovery-btn swipe-discovery-btn--skip" onClick={() => advance('left')} disabled={!topBook}>
            ⬅ Skip
          </button>
          <button type="button" className="swipe-discovery-btn swipe-discovery-btn--like" onClick={() => advance('right')} disabled={!topBook}>
            Save ➜
          </button>
        </div>
        {loading && <p className="swipe-discovery-loading">Loading more books...</p>}
      </div>
    </div>
  );
}

export default SwipeDiscovery;
